// @ts-nocheck
// Ported from obd3-gui v1 (src/features/dashboard/use-dashboard-layout.ts).
// Persists per-user per-tab widget layout to localStorage.
//
// v2 adjustment: role + userId come from Storage.user, not useAuthStore
// (the Java-backend store is empty for v1-flow logins — same reason
// Header.tsx and filter-nav.ts already read from Storage).
import { useCallback, useEffect, useState } from 'react';
import { getUser } from '@/utilities/setting';
import {
  WIDGETS,
  defaultLayoutFor,
  type LayoutItem,
  type LayoutScope,
  type WidgetId,
  type WidgetSize,
} from './config';

// Bump on any breaking default-layout change so previously-saved
// per-user layouts stop shadowing the new seed.
//   v1 → initial (Phase A)
//   v2 → content-aware sizing overhaul (tables → xl full-width,
//        metric grids → md/lg, small alerts → sm)
//   v3 → Users&Campaigns / Disconnected / Cert Expiry bumped to xl
//   v4 → Agent Status Distribution bumped to xl (was lg)
const STORAGE_VERSION = 4;

function storageKey(userId: string | null, scope: LayoutScope) {
  return `ct3:dashboard:v${STORAGE_VERSION}:${userId ?? 'anon'}:${scope}`;
}

function isValidItem(it: unknown): it is LayoutItem {
  return (
    typeof it === 'object' &&
    it !== null &&
    'id' in it &&
    'size' in it &&
    WIDGETS.some((w) => w.id === (it as { id: WidgetId }).id)
  );
}

// KPI tiles are always `xs` — the strip's shape is a product decision,
// not a user preference. Coerce older stored sizes to `xs` on load.
function coerceKpiSizes(items: LayoutItem[], scope: LayoutScope): LayoutItem[] {
  if (scope !== 'kpi') return items;
  return items.map((it) => (it.size === 'xs' ? it : { ...it, size: 'xs' }));
}

function loadLayout(userId: string | null, role: string, scope: LayoutScope): LayoutItem[] {
  if (typeof window === 'undefined') return defaultLayoutFor(role, scope);
  try {
    const raw = window.localStorage.getItem(storageKey(userId, scope));
    if (!raw) return defaultLayoutFor(role, scope);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultLayoutFor(role, scope);
    return coerceKpiSizes(parsed.filter(isValidItem), scope);
  } catch {
    return defaultLayoutFor(role, scope);
  }
}

function saveLayout(userId: string | null, scope: LayoutScope, layout: LayoutItem[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userId, scope), JSON.stringify(layout));
  } catch {
    /* quota / disabled — ignore */
  }
}

function useResolvedUser(): { userId: string | null; role: string } {
  const [ident, setIdent] = useState(() => {
    const u = getUser();
    return { userId: u?.id ?? null, role: u?.role ?? '' };
  });
  useEffect(() => {
    const u = getUser();
    setIdent({ userId: u?.id ?? null, role: u?.role ?? '' });
  }, []);
  return ident;
}

export function useDashboardLayout(scope: LayoutScope) {
  const { userId, role } = useResolvedUser();

  const [layout, setLayout] = useState<LayoutItem[]>(() =>
    loadLayout(userId, role, scope),
  );

  useEffect(() => {
    setLayout(loadLayout(userId, role, scope));
  }, [userId, role, scope]);

  const replace = useCallback(
    (next: LayoutItem[]) => {
      setLayout(next);
      saveLayout(userId, scope, next);
    },
    [userId, scope],
  );

  const reorder = useCallback(
    (fromId: WidgetId, toId: WidgetId) => {
      const fromIdx = layout.findIndex((it) => it.id === fromId);
      const toIdx = layout.findIndex((it) => it.id === toId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const next = [...layout];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved!);
      replace(next);
    },
    [layout, replace],
  );

  const setSize = useCallback(
    (id: WidgetId, size: WidgetSize) => {
      replace(layout.map((it) => (it.id === id ? { ...it, size } : it)));
    },
    [layout, replace],
  );

  const add = useCallback(
    (id: WidgetId) => {
      const desc = WIDGETS.find((w) => w.id === id);
      if (!desc) return;
      const exists = layout.some((it) => it.id === id);
      if (exists) return;
      replace([...layout, { id, size: desc.defaultSize }]);
    },
    [layout, replace],
  );

  const remove = useCallback(
    (id: WidgetId) => {
      replace(layout.filter((it) => it.id !== id));
    },
    [layout, replace],
  );

  const reset = useCallback(() => {
    const next = defaultLayoutFor(role, scope);
    setLayout(next);
    saveLayout(userId, scope, next);
  }, [role, scope, userId]);

  return { layout, replace, reorder, setSize, add, remove, reset, role };
}
